/**
 * 工具函数集合
 */

import test from './test';
import { round } from './digit';
import user from '@/sheep/store/user';

/**
 * 如果 value 小于 min，取 min；如果 value 大于 max，取 max
 * @param min 最小值
 * @param max 最大值
 * @param value 当前值
 * @returns 区间内的值
 */
function range(min = 0, max = 0, value : number | string = 0) : number {
	return Math.max(min, Math.min(max, Number(value)));
}

/**
 * 用于获取用户传递值的 px 值
 * 如果用户传递了 "xxpx" 或者 "xxrpx"，取出其数值部分，如果是 "xxxrpx" 还需要用 uni.upx2px 进行转换
 * @param value 用户传递值的 px 值
 * @param unit 是否返回带单位的字符串
 * @returns px 值或带单位的字符串
 */
export function getPx(value : number | string, unit = false) : number | string {
	if (test.number(value)) {
		return unit ? `${value}px` : Number(value);
	}
	// 如果带有 rpx，先取出其数值部分，再转为 px 值
	if (/(rpx|upx)$/.test(value as string)) {
		const px = uni.upx2px(parseInt(value as string));
		return unit ? `${px}px` : px;
	}
	return unit ? `${parseInt(value as string)}px` : parseInt(value as string);
}

/**
 * 进行延时
 * @param value 堵塞时间，单位 ms 毫秒
 * @returns Promise
 */
export function sleep(value = 30) : Promise<void> {
	return new Promise((resolve) => {
		setTimeout(() => {
			resolve();
		}, value);
	});
}

/**
 * 运行期判断平台
 * @returns 返回所在平台(小写)
 */
export function os() : string {
	return uni.getDeviceInfo().platform.toLowerCase();
}

/**
 * 取一个区间数
 * @param min 最小值
 * @param max 最大值
 * @returns 随机数
 */
function random(min : number, max : number) : number {
	if (min >= 0 && max > 0 && max >= min) {
		const gab = max - min + 1;
		return Math.floor(Math.random() * gab + min);
	}
	return 0;
}

/**
 * 生成 uuid
 * @param len uuid 的长度
 * @param firstU 将返回的首字母置为 "u"
 * @param radix 生成 uuid 的基数
 * @returns uuid 字符串
 */
export function guid(len = 32, firstU = true, radix : number | null = null) : string {
	const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'.split('');
	const uuid : string[] = [];
	radix = radix || chars.length;

	if (len) {
		for (let i = 0; i < len; i++) uuid[i] = chars[0 | (Math.random() * radix)];
	} else {
		let r;
		uuid[8] = uuid[13] = uuid[18] = uuid[23] = '-';
		uuid[14] = '4';

		for (let i = 0; i < 36; i++) {
			if (!uuid[i]) {
				r = 0 | (Math.random() * 16);
				uuid[i] = chars[i === 19 ? (r & 0x3) | 0x8 : r];
			}
		}
	}
	// 移除第一个字符，并用 u 替代
	if (firstU) {
		uuid.shift();
		return `u${uuid.join('')}`;
	}
	return uuid.join('');
}

/**
 * 获取父组件的参数
 * @param name 父组件的参数名
 * @returns 父组件实例
 */
export function $parent(this : any, name : string | undefined = undefined) : any {
	let parent = this.$parent;
	while (parent) {
		if (parent.$options && parent.$options.name !== name) {
			parent = parent.$parent;
		} else {
			return parent;
		}
	}
	return false;
}

/**
 * 样式转换 - 对象转字符串，或者字符串转对象
 * @param customStyle 需要转换的目标
 * @param target 转换的目的，object-转为对象，string-转为字符串
 * @returns 转换后的样式
 */
export function addStyle(customStyle : object | string, target : 'object' | 'string' = 'object') : object | string {
	if (
		test.empty(customStyle) ||
		(typeof customStyle === 'object' && target === 'object') ||
		(target === 'string' && typeof customStyle === 'string')
	) {
		return customStyle;
	}
	// 字符串转对象
	if (target === 'object') {
		const styleStr = trim(customStyle as string);
		const styleArray = styleStr.split(';');
		const style : Record<string, string> = {};
		for (let i = 0; i < styleArray.length; i++) {
			if (styleArray[i]) {
				const item = styleArray[i].split(':');
				style[trim(item[0])] = trim(item[1]);
			}
		}
		return style;
	}
	// 对象转字符串
	let str = '';
	for (const i in customStyle as Record<string, any>) {
		const key = i.replace(/([A-Z])/g, '-$1').toLowerCase();
		str += `${key}:${(customStyle as Record<string, any>)[i]};`;
	}
	return trim(str);
}

/**
 * 添加单位，如果有 rpx，upx，%，px 等单位结尾或者值为 auto，直接返回，否则加上 px 单位结尾
 * @param value 需要添加单位的值
 * @param unit 添加的单位名
 */
export function addUnit(value : string | number = 'auto', unit = 'px') : string {
	const strValue = String(value);
	return test.number(strValue) ? `${strValue}${unit}` : strValue;
}

/**
 * 深度克隆
 * @param obj 需要深度克隆的对象
 * @returns 克隆后的对象或者原值（不是对象）
 */
function deepClone<T>(obj : T) : T {
	if ([null, undefined, NaN, false].includes(obj as any)) return obj;
	if (typeof obj !== 'object' && typeof obj !== 'function') {
		return obj;
	}
	const o : any = test.array(obj) ? [] : {};
	for (const i in obj as any) {
		if ((obj as any).hasOwnProperty(i)) {
			o[i] = typeof (obj as any)[i] === 'object' ? deepClone((obj as any)[i]) : (obj as any)[i];
		}
	}
	return o;
}

/**
 * JS 对象深度合并
 * @param target 需要拷贝的对象
 * @param source 拷贝的来源对象
 * @returns 深度合并后的对象或者 false（入参有不是对象）
 */
export function deepMerge<T extends Record<string, any>>(target : T, source : Partial<T>) : T | false {
	target = deepClone(target);
	if (typeof target !== 'object' || typeof source !== 'object') return false;
	for (const prop in source) {
		if (!source.hasOwnProperty(prop)) continue;
		if (prop in target) {
			if (typeof target[prop] !== 'object') {
				target[prop] = source[prop] as any;
			} else if (typeof source[prop] !== 'object') {
				target[prop] = source[prop] as any;
			} else if (target[prop].concat && source[prop].concat) {
				target[prop] = target[prop].concat(source[prop]);
			} else {
				target[prop] = deepMerge(target[prop], source[prop] as any);
			}
		} else {
			target[prop] = source[prop] as any;
		}
	}
	return target;
}

/**
 * error 提示
 * @param err 错误内容
 */
function error(err : any) : void {
	if (process.env.NODE_ENV === 'development') {
		console.error(`SheepJS:${err}`);
	}
}

/**
 * 打乱数组
 * @param array 需要打乱的数组
 * @returns 打乱后的数组
 */
function randomArray<T>(array : T[] = []) : T[] {
	return array.sort(() => Math.random() - 0.5);
}

// padStart 的 polyfill
if (!String.prototype.padStart) {
	String.prototype.padStart = function (maxLength : number, fillString : string = ' ') : string {
		if (Object.prototype.toString.call(fillString) !== '[object String]') {
			throw new TypeError('fillString must be String');
		}
		const str = this;
		if (str.length >= maxLength) return String(str);

		const fillLength = maxLength - str.length;
		let times = Math.ceil(fillLength / fillString.length);
		while ((times >>= 1)) {
			fillString += fillString;
			if (times === 1) {
				fillString += fillString;
			}
		}
		return fillString.slice(0, fillLength) + str;
	};
}

/**
 * 格式化时间
 * @param dateTime 需要格式化的时间戳
 * @param formatStr 格式化规则
 * @returns 返回格式化后的字符串
 */
function timeFormat(dateTime : string | number | null = null, formatStr = 'yyyy-mm-dd') : string {
	let date : Date;
	if (!dateTime) {
		date = new Date();
	} else if (/^\d{10}$/.test((dateTime as any)?.toString().trim())) {
		date = new Date((dateTime as number) * 1000);
	} else if (typeof dateTime === 'string' && /^\d+$/.test(dateTime.trim())) {
		date = new Date(Number(dateTime));
	} else {
		date = new Date(typeof dateTime === 'string' ? dateTime.replace(/-/g, '/') : dateTime as any);
	}

	const timeSource : Record<string, string> = {
		y: date.getFullYear().toString(),
		m: (date.getMonth() + 1).toString().padStart(2, '0'),
		d: date.getDate().toString().padStart(2, '0'),
		h: date.getHours().toString().padStart(2, '0'),
		M: date.getMinutes().toString().padStart(2, '0'),
		s: date.getSeconds().toString().padStart(2, '0'),
	};

	let result = formatStr;
	for (const key in timeSource) {
		const [ret] = new RegExp(`${key}+`).exec(result) || [];
		if (ret) {
			const beginIndex = key === 'y' && ret.length === 2 ? 2 : 0;
			result = result.replace(ret, timeSource[key].slice(beginIndex));
		}
	}
	return result;
}

/**
 * 时间戳转为多久之前
 * @param timestamp 时间戳
 * @param format 格式化规则
 * @returns 转化后的内容
 */
function timeFrom(timestamp : string | number | null = null, format : string | boolean = 'yyyy-mm-dd') : string {
	if (timestamp == null) timestamp = Number(new Date());
	let ts = parseInt(timestamp as string);
	if (ts.toString().length === 10) ts *= 1000;
	let timer = new Date().getTime() - ts;
	timer = parseInt(String(timer / 1000));

	let tips = '';
	switch (true) {
		case timer < 300:
			tips = '刚刚';
			break;
		case timer >= 300 && timer < 3600:
			tips = `${parseInt(String(timer / 60))}分钟前`;
			break;
		case timer >= 3600 && timer < 86400:
			tips = `${parseInt(String(timer / 3600))}小时前`;
			break;
		case timer >= 86400 && timer < 2592000:
			tips = `${parseInt(String(timer / 86400))}天前`;
			break;
		default:
			if (format === false) {
				if (timer >= 2592000 && timer < 365 * 86400) {
					tips = `${parseInt(String(timer / (86400 * 30)))}个月前`;
				} else {
					tips = `${parseInt(String(timer / (86400 * 365)))}年前`;
				}
			} else {
				tips = timeFormat(ts, format as string);
			}
	}
	return tips;
}

/**
 * 去除空格
 * @param str 需要去除空格的字符串
 * @param pos both(左右)|left|right|all 默认 both
 */
function trim(str : string, pos : 'both' | 'left' | 'right' | 'all' = 'both') : string {
	str = String(str);
	if (pos === 'both') {
		return str.replace(/^\s+|\s+$/g, '');
	}
	if (pos === 'left') {
		return str.replace(/^\s*/, '');
	}
	if (pos === 'right') {
		return str.replace(/(\s*$)/g, '');
	}
	if (pos === 'all') {
		return str.replace(/\s+/g, '');
	}
	return str;
}

/**
 * 对象转 url 参数
 * @param data 对象
 * @param isPrefix 是否自动加上 "?"
 * @param arrayFormat 规则 indices|brackets|repeat|comma
 */
function queryParams(data : Record<string, any> = {}, isPrefix = true, arrayFormat : 'indices' | 'brackets' | 'repeat' | 'comma' = 'brackets') : string {
	const prefix = isPrefix ? '?' : '';
	const _result : string[] = [];
	if (['indices', 'brackets', 'repeat', 'comma'].indexOf(arrayFormat) === -1)
		arrayFormat = 'brackets';
	for (const key in data) {
		const value = data[key];
		if (['', undefined, null].indexOf(value) >= 0) {
			continue;
		}
		if (value.constructor === Array) {
			switch (arrayFormat) {
				case 'indices':
					for (let i = 0; i < value.length; i++) {
						_result.push(`${key}[${i}]=${value[i]}`);
					}
					break;
				case 'brackets':
					value.forEach((_value : any) => {
						_result.push(`${key}[]=${_value}`);
					});
					break;
				case 'repeat':
					value.forEach((_value : any) => {
						_result.push(`${key}=${_value}`);
					});
					break;
				case 'comma':
					let commaStr = '';
					value.forEach((_value : any) => {
						commaStr += (commaStr ? ',' : '') + _value;
					});
					_result.push(`${key}=${commaStr}`);
					break;
				default:
					value.forEach((_value : any) => {
						_result.push(`${key}[]=${_value}`);
					});
			}
		} else {
			_result.push(`${key}=${value}`);
		}
	}
	return _result.length ? prefix + _result.join('&') : '';
}

/**
 * 显示消息提示框
 * @param title 提示的内容
 * @param duration 提示的延迟时间，单位毫秒
 */
function toast(title : string, duration = 1000) : void {
	uni.showToast({
		title: String(title),
		icon: 'none',
		duration,
	});
}

/**
 * 根据主题 type 值，获取对应的图标
 * @param type 主题名称
 * @param fill 是否使用 fill 填充实体的图标
 */
function type2icon(type = 'success', fill = false) : string {
	const types = ['primary', 'info', 'error', 'warning', 'success'];
	if (!types.includes(type)) type = 'success';
	let iconName = '';
	switch (type) {
		case 'primary':
		case 'info':
			iconName = 'info-circle';
			break;
		case 'error':
			iconName = 'close-circle';
			break;
		case 'warning':
			iconName = 'error-circle';
			break;
		case 'success':
			iconName = 'checkmark-circle';
			break;
	}
	if (fill) iconName += '-fill';
	return iconName;
}

/**
 * 数字格式化
 * @param number 要格式化的数字
 * @param decimals 保留几位小数
 * @param decimalPoint 小数点符号
 * @param thousandsSeparator 千分位符号
 * @returns 格式化后的数字
 */
function priceFormat(number : number | string, decimals = 0, decimalPoint = '.', thousandsSeparator = ',') : string {
	number = `${number}`.replace(/[^0-9+-Ee.]/g, '');
	const n = !isFinite(+number) ? 0 : +number;
	const prec = !isFinite(+decimals) ? 0 : Math.abs(decimals);
	const sep = thousandsSeparator;
	const dec = decimalPoint;
	let s = '';

	s = (prec ? round(n, prec) + '' : `${Math.round(n)}`).split('.');
	const re = /(-?\d+)(\d{3})/;
	while (re.test(s[0])) {
		s[0] = s[0].replace(re, `$1${sep}$2`);
	}

	if ((s[1] || '').length < prec) {
		s[1] = s[1] || '';
		s[1] += new Array(prec - s[1].length + 1).join('0');
	}
	return s.join(dec);
}

/**
 * 获取 duration 值
 * @param value 值，例如: "1s"|"100ms"|1|100
 * @param unit 是否返回带单位的字符串
 * @returns duration 值
 */
function getDuration(value : string | number, unit = true) : string | number {
	const valueNum = parseInt(String(value));
	if (unit) {
		if (/s$/.test(String(value))) return value;
		return valueNum > 30 ? `${value}ms` : `${value}s`;
	}
	if (/ms$/.test(String(value))) return valueNum;
	if (/s$/.test(String(value))) return valueNum > 30 ? valueNum : valueNum * 1000;
	return valueNum;
}

/**
 * 日期的月或日补零操作
 * @param value 需要补零的值
 */
function padZero(value : number | string) : string {
	return `00${value}`.slice(-2);
}

/**
 * 获取某个对象下的属性
 * @param obj 对象
 * @param key 需要获取的属性字段
 */
function getProperty(obj : any, key : string) : any {
	if (!obj) {
		return undefined;
	}
	if (typeof key !== 'string' || key === '') {
		return '';
	}
	if (key.indexOf('.') !== -1) {
		const keys = key.split('.');
		let firstObj = obj[keys[0]] || {};
		for (let i = 1; i < keys.length; i++) {
			if (firstObj) {
				firstObj = firstObj[keys[i]];
			}
		}
		return firstObj;
	}
	return obj[key];
}

/**
 * 设置对象的属性值
 * @param obj 对象
 * @param key 需要设置的属性
 * @param value 设置的值
 */
function setProperty(obj : any, key : string, value : any) : void {
	if (!obj) {
		return;
	}
	const inFn = function (_obj : any, keys : string[], v : any) {
		if (keys.length === 1) {
			_obj[keys[0]] = v;
			return;
		}
		while (keys.length > 1) {
			const k = keys[0];
			if (!_obj[k] || typeof _obj[k] !== 'object') {
				_obj[k] = {};
			}
			keys.shift();
			inFn(_obj[k], keys, v);
		}
	};

	if (typeof key !== 'string' || key === '') {
		return;
	} else if (key.indexOf('.') !== -1) {
		const keys = key.split('.');
		inFn(obj, keys, value);
	} else {
		obj[key] = value;
	}
}

/**
 * 获取当前页面路径
 * @param withParams 是否携带参数，默认为 false
 */
function page(withParams = false) : string {
	const pages = getCurrentPages();
	const currentPage = pages[pages.length - 1];
	if (!currentPage) return '';
	
	const route = `/${currentPage.route || ''}`;
	
	if (withParams) {
		const options = (currentPage as any).options || {};
		const params = Object.keys(options)
			.map(key => `${key}=${encodeURIComponent(options[key])}`)
			.join('&');
		return params ? `${route}?${params}` : route;
	}
	
	return route;
}

/**
 * 检查用户是否已登录，未登录则自动跳转到登录页
 * @param redirect 登录成功后是否重定向回当前页面，默认为 true
 * @returns 是否已登录
 */
export function checkLogin(redirect = true) : boolean {
	const userStore = user();

	if (userStore.isLogin) {
		return true;
	}

	// 未登录，保存当前页面路径（包含参数）
	if (redirect) {
		const currentPage = page(true);
		uni.setStorageSync('redirect_url', currentPage);
	}

	// 跳转到登录页
	uni.navigateTo({
		url: '/pages-sub/login/login'
	});

	return false;
}

/**
 * 获取当前路由栈实例数组
 */
function pages() : any[] {
	return getCurrentPages();
}

/**
 * 获取 H5 真实根地址，兼容 hash+history 模式
 */
export function getRootUrl() : string {
	let url = '';
	// #ifdef H5
	url = location.origin;
	if (location.hash !== '') {
		url += '#/';
	} else {
		url += '/';
	}
	// #endif
	return url;
}

/**
 * 多端复制文本
 * @param text 要复制的文本
 */
export function copyText(text : string) : void {
	// #ifndef H5
	uni.setClipboardData({
		data: text,
		success: function () {
			toast('复制成功!');
		},
		fail: function () {
			toast('复制失败!');
		},
	});
	// #endif
	// #ifdef H5
	const createInput = document.createElement('textarea');
	createInput.value = text;
	document.body.appendChild(createInput);
	createInput.select();
	document.execCommand('Copy');
	createInput.className = 'createInput';
	createInput.style.display = 'none';
	toast('复制成功');
	// #endif
}

export default {
	range,
	getPx,
	sleep,
	os,
	random,
	guid,
	$parent,
	addStyle,
	addUnit,
	deepClone,
	deepMerge,
	error,
	randomArray,
	timeFormat,
	timeFrom,
	trim,
	queryParams,
	toast,
	type2icon,
	priceFormat,
	getDuration,
	padZero,
	getProperty,
	setProperty,
	page,
	pages,
	test,
	getRootUrl,
	copyText,
	checkLogin,
};